import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import CreditBalance from "@/components/CreditBalance";
import AdvisorSessionsInline from "@/components/AdvisorSessionsInline";
import ContinueOnDesktopNotice from "@/components/ContinueOnDesktopNotice";
import LowCreditNudge from "@/components/LowCreditNudge";
import David from "@/components/David";
import { extractErrorMessage } from "@/lib/apiError";
import { openPaystackPopup } from "@/lib/paystackInline";
import {
  ArrowLeft, Sparkle, CheckCircle, Warning, ChartBar, Calculator, Scales, House, Target,
} from "@phosphor-icons/react";

const C = {
  bg: "#000",
  ink: "#0A2826",
  panel: "#103532",
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
  amber: "#FCD34D",
  green: "#9AE6B4",
  red: "#F87171",
};

const fmt = (n, { compact = true } = {}) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (compact && abs >= 1_000_000) return `$ ${(n / 1_000_000).toFixed(2)}M`;
  if (compact && abs >= 1_000) return `$ ${(n / 1_000).toFixed(0)}k`;
  return `$ ${Math.round(n).toLocaleString()}`;
};

export default function ValuationBuilder() {
  const { valuationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [doc, setDoc] = useState(null);
  const [calc, setCalc] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [finalising, setFinalising] = useState(false);
  const saveTimer = useRef(null);

  // initial load
  useEffect(() => {
    (async () => {
      try {
        const [{ data: m }, { data: d }] = await Promise.all([
          api.get("/valuations/meta"),
          api.get(`/valuations/${valuationId}`),
        ]);
        setMeta(m);
        setDoc(d);
      } catch {
        toast.error("Valuation not found");
        navigate("/dashboard");
      }
    })();
  }, [valuationId, navigate]);

  // live preview — debounced
  const recalc = useCallback(async () => {
    try {
      const { data } = await api.get(`/valuations/${valuationId}/calculate`);
      setCalc(data);
    } catch {
      /* ignore */
    }
  }, [valuationId]);

  useEffect(() => {
    if (!doc) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(recalc, 350);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [doc, recalc]);

  const patch = async (partial) => {
    setDoc((p) => ({ ...p, ...partial }));
    try {
      await api.patch(`/valuations/${valuationId}`, partial);
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const patchInputs = (key, value) => {
    const newInputs = { ...(doc.inputs || {}), [key]: value };
    patch({ inputs: newInputs });
  };

  const isPre = doc?.stage === "pre_revenue";

  const finalise = async () => {
    if (!window.confirm("This will use 50 credits and lock in your valuation report. Continue?")) return;
    setFinalising(true);
    try {
      const { data } = await api.post(`/valuations/${valuationId}/finalise`);
      setDoc((p) => ({ ...p, finalised_at: new Date().toISOString(), final_result: data.result }));
      setCalc(data.result);
      setActiveTab("report");
      window.dispatchEvent(new Event("clinq:credits-changed"));
      toast.success("Valuation finalised · 50 credits used");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.isCreditShortage) {
        // Global api.js interceptor already showed the Buy credits toast.
      } else {
        toast.error(typeof d === "string" ? d : "Could not finalise");
      }
    } finally {
      setFinalising(false);
    }
  };

  const importFromModel = async () => {
    try {
      const { data } = await api.post(`/valuations/${valuationId}/import-from-model`);
      // Refresh the doc to pick up the imported inputs
      const { data: fresh } = await api.get(`/valuations/${valuationId}`);
      setDoc(fresh);
      toast.success(`Imported ${data.imported_keys.length} fields from ${data.model_name}`);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not import from your financial model."));
    }
  };

  const downloadReport = async () => {
    try {
      const res = await api.get(`/valuations/${valuationId}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Clinq_Valuation_${(doc.company_name || "report").replace(/[^a-z0-9]+/gi, "_")}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download report");
    }
  };

  const requestReview = async () => {
    if (!window.confirm("This will open Paystack to pay USD 175. Continue?")) return;
    try {
      const callbackUrl = `${window.location.origin}/payment/callback`;
      const { data } = await api.post(`/valuations/${valuationId}/review/checkout`,
        { callback_url: callbackUrl, currency: "USD" });
      // Iter83: prefer Paystack Inline popup (fallback to full redirect
      // via `authorization_url` if the inline script is blocked).
      await openPaystackPopup({
        accessCode: data.access_code,
        publicKey: data.public_key,
        reference: data.reference,
        authorizationUrl: data.authorization_url,
        onSuccess: () => {
          window.location.href = `${callbackUrl}?reference=${encodeURIComponent(data.reference)}`;
        },
        onClose: () => {
          toast.info("Checkout closed. You can try again anytime.");
        },
      });
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not start review checkout"));
    }
  };

  if (!doc || !meta) {
    return <div className="min-h-screen grid place-items-center text-white/50" style={{ backgroundColor: C.bg }}>Loading…</div>;
  }

  const TABS = [
    { id: "profile", label: "Profile", icon: House },
    { id: "inputs", label: "Inputs", icon: Calculator },
    isPre ? { id: "berkus", label: "Berkus", icon: Target } : null,
    isPre ? { id: "scorecard", label: "Scorecard", icon: ChartBar } : null,
    isPre ? null : { id: "financials", label: "Financials & DCF", icon: ChartBar },
    isPre ? null : { id: "balance_sheet", label: "Balance sheet", icon: Scales },
    { id: "report", label: "Report", icon: Sparkle },
  ].filter(Boolean);

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bg, fontFamily: "Manrope, sans-serif" }} data-testid="valuation-builder">
      {/* Header */}
      <header className="border-b sticky top-0 z-30" style={{ borderColor: "rgba(123,211,206,0.12)", backgroundColor: "rgba(0,0,0,0.92)", backdropFilter: "blur(8px)" }}>
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white" data-testid="back-to-dashboard">
            <ArrowLeft size={14} /> Dashboard
          </Link>
          <div className="flex-1 text-center">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: C.amber }}>Business valuation</div>
            <div className="font-display font-extrabold text-base" data-testid="valuation-title">{doc.company_name}</div>
          </div>
          <div className="flex items-center gap-3">
            {doc.finalised_at && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full"
                style={{ backgroundColor: `${C.green}1f`, color: C.green, border: `1px solid ${C.green}55` }}
                data-testid="valuation-finalised-badge">
                <CheckCircle size={11} weight="fill" /> Finalised
              </span>
            )}
            <CreditBalance variant="dark" />
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-6 pb-3 flex gap-1 overflow-x-auto" data-testid="valuation-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              data-testid={`tab-${t.id}`}
              className="text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-2 rounded-md transition-colors inline-flex items-center gap-1.5"
              style={{
                backgroundColor: activeTab === t.id ? C.amber : "transparent",
                color: activeTab === t.id ? "#000" : "rgba(255,255,255,0.6)",
              }}>
              <t.icon size={12} weight="bold" /> {t.label}
            </button>
          ))}
        </nav>
      </header>

      <ContinueOnDesktopNotice surface="the valuation builder" />

      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <main className="lg:col-span-8">
          {activeTab === "profile" && <ProfileTab doc={doc} meta={meta} patch={patch} />}
          {activeTab === "inputs" && (
            <>
              <div className="flex justify-end mb-3">
                <button onClick={importFromModel} data-testid="import-from-model-btn"
                  className="text-xs font-bold uppercase tracking-[0.15em] px-3 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors"
                  style={{ border: `1px solid ${C.tealSoft}55`, color: C.tealSoft, backgroundColor: "rgba(123,211,206,0.05)" }}
                  title="Pull TTM revenue, EBITDA, 5-year FCF and balance sheet from your latest financial model">
                  ↓ Import from financial model
                </button>
              </div>
              <InputsTab doc={doc} patchInputs={patchInputs} isPre={isPre} />
            </>
          )}
          {activeTab === "berkus" && <BerkusTab doc={doc} meta={meta} patchInputs={patchInputs} />}
          {activeTab === "scorecard" && <ScorecardTab doc={doc} meta={meta} patchInputs={patchInputs} />}
          {activeTab === "financials" && <FinancialsTab doc={doc} patchInputs={patchInputs} />}
          {activeTab === "balance_sheet" && <BalanceSheetTab doc={doc} patchInputs={patchInputs} />}
          {activeTab === "report" && (
            <>
              {doc.finalised_at && (
                <div className="rounded-2xl p-5 mb-4 border flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  style={{ borderColor: `${C.orange}40`, backgroundColor: "rgba(245,158,11,0.04)" }}
                  data-testid="valuation-actions">
                  <div className="text-[13px]" style={{ color: "rgba(255,255,255,0.78)" }}>
                    Your valuation is locked in. Download the report or send it to a Clinq advisor for paid review.
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={downloadReport} data-testid="download-report-btn"
                      className="text-xs font-bold uppercase tracking-[0.15em] px-4 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors"
                      style={{ border: `1px solid ${C.tealSoft}55`, color: C.tealSoft, backgroundColor: "rgba(123,211,206,0.05)" }}>
                      ↓ Download .docx
                    </button>
                    {doc.advisor_review_paid_at ? (
                      <span className="text-xs font-bold uppercase tracking-[0.15em] px-4 py-2 rounded-md inline-flex items-center gap-1.5"
                        style={{ backgroundColor: `${C.green}1f`, color: C.green, border: `1px solid ${C.green}55` }}
                        data-testid="advisor-review-paid">
                        ✓ Advisor review requested
                      </span>
                    ) : (
                      <button onClick={requestReview} data-testid="request-advisor-review-btn"
                        className="text-xs font-bold uppercase tracking-[0.15em] px-4 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors"
                        style={{ backgroundColor: C.orange, color: "#000" }}>
                        Send to advisor for review · $175
                      </button>
                    )}
                  </div>
                </div>
              )}
              <ReportTab doc={doc} calc={doc.finalised_at ? doc.final_result : calc} />
            </>
          )}
        </main>

        {/* RIGHT — live preview rail */}
        <aside className="lg:col-span-4 lg:sticky lg:top-32 h-fit space-y-4" data-testid="valuation-preview-rail">
          <AdvisorSessionsInline compact />
          <LivePreview calc={calc} doc={doc} onFinalise={finalise} finalising={finalising} />
        </aside>
      </div>
      <LowCreditNudge />
      <David />
    </div>
  );
}

// =============================================================== PROFILE TAB
function ProfileTab({ doc, meta, patch }) {
  return (
    <SectionCard title="Profile" subtitle="Sector, stage and sub-stage drive which valuation methods we apply.">
      <Row label="Company name">
        <input type="text" value={doc.company_name || ""}
          onChange={(e) => patch({ company_name: e.target.value })}
          data-testid="profile-company"
          className="w-full px-3 py-2 rounded-md border focus:outline-none"
          style={{ backgroundColor: "rgba(0,0,0,0.45)", borderColor: "rgba(255,255,255,0.1)", color: "#fff" }} />
      </Row>
      <Row label="Stage">
        <select value={doc.stage || "post_revenue"}
          onChange={(e) => {
            const stage = e.target.value;
            const sub = stage === "pre_revenue" ? "mvp" : "growth";
            patch({ stage, sub_stage: sub });
          }}
          data-testid="profile-stage"
          className="w-full px-3 py-2 rounded-md border focus:outline-none"
          style={{ backgroundColor: "rgba(0,0,0,0.45)", borderColor: "rgba(255,255,255,0.1)", color: "#fff" }}>
          {meta.stages.map((s) => <option key={s.id} value={s.id} style={{ backgroundColor: C.ink }}>{s.label}</option>)}
        </select>
      </Row>
      <Row label="Sub-stage" hint="Adjusts the default method weights.">
        <select value={doc.sub_stage || "growth"}
          onChange={(e) => patch({ sub_stage: e.target.value })}
          data-testid="profile-substage"
          className="w-full px-3 py-2 rounded-md border focus:outline-none"
          style={{ backgroundColor: "rgba(0,0,0,0.45)", borderColor: "rgba(255,255,255,0.1)", color: "#fff" }}>
          {(meta.stages.find((s) => s.id === doc.stage)?.sub_stages || []).map((ss) => (
            <option key={ss} value={ss} style={{ backgroundColor: C.ink }}>{ss.replace("_", " ")}</option>
          ))}
        </select>
      </Row>
      <Row label="Sector">
        <select value={doc.sector || "other"}
          onChange={(e) => patch({ sector: e.target.value })}
          data-testid="profile-sector"
          className="w-full px-3 py-2 rounded-md border focus:outline-none"
          style={{ backgroundColor: "rgba(0,0,0,0.45)", borderColor: "rgba(255,255,255,0.1)", color: "#fff" }}>
          {meta.sectors.map((s) => <option key={s.id} value={s.id} style={{ backgroundColor: C.ink }}>{s.label}</option>)}
        </select>
      </Row>
      <Row label="Country of primary operations">
        <input type="text" value={doc.country || ""}
          onChange={(e) => patch({ country: e.target.value })}
          data-testid="profile-country"
          placeholder="e.g. Kenya"
          className="w-full px-3 py-2 rounded-md border focus:outline-none"
          style={{ backgroundColor: "rgba(0,0,0,0.45)", borderColor: "rgba(255,255,255,0.1)", color: "#fff" }} />
      </Row>
    </SectionCard>
  );
}

// =============================================================== INPUTS TAB
function InputsTab({ doc, patchInputs, isPre }) {
  const i = doc.inputs || {};
  if (isPre) {
    return (
      <SectionCard title="Pre-revenue inputs" subtitle="Cost-to-duplicate sets the absolute floor of your valuation.">
        <Row label="Cumulative burn to date ($)" hint="Sum of monthly losses since founding">
          <NumberInput value={i.cumulative_burn} onChange={(v) => patchInputs("cumulative_burn", v)} testid="input-burn" />
        </Row>
        <Row label="Capex to date ($)" hint="One-off equipment, IP registration, build costs">
          <NumberInput value={i.capex_to_date} onChange={(v) => patchInputs("capex_to_date", v)} testid="input-capex" />
        </Row>
        <Row label="Cash on hand ($)">
          <NumberInput value={i.cash} onChange={(v) => patchInputs("cash", v)} testid="input-cash-pre" />
        </Row>
      </SectionCard>
    );
  }
  return (
    <SectionCard title="Revenue quality" subtitle="Drives the Revenue Multiple method (Methodology Section 5.1).">
      <Row label="TTM revenue ($)" hint="Trailing 12 months, gross revenue">
        <NumberInput value={i.ttm_revenue} onChange={(v) => patchInputs("ttm_revenue", v)} testid="input-revenue" />
      </Row>
      <Row label="YoY growth (%)" hint="Latest year vs prior year">
        <NumberInput value={i.growth_pct} onChange={(v) => patchInputs("growth_pct", v)} testid="input-growth" />
      </Row>
      <Row label="Gross margin (%)" hint="(Revenue − COGS) ÷ Revenue">
        <NumberInput value={i.gross_margin_pct} onChange={(v) => patchInputs("gross_margin_pct", v)} testid="input-margin" />
      </Row>
      <Row label="Top-3 customer concentration (%)" hint="Share of total revenue from top 3 customers">
        <NumberInput value={i.top_customers_pct} onChange={(v) => patchInputs("top_customers_pct", v)} testid="input-concentration" />
      </Row>
      <Row label="Revenue type">
        <select value={i.revenue_type || "recurring"}
          onChange={(e) => patchInputs("revenue_type", e.target.value)}
          data-testid="input-revtype"
          className="w-full px-3 py-2 rounded-md border focus:outline-none"
          style={{ backgroundColor: "rgba(0,0,0,0.45)", borderColor: "rgba(255,255,255,0.1)", color: "#fff" }}>
          <option value="recurring" style={{ backgroundColor: C.ink }}>Recurring / subscription</option>
          <option value="mixed" style={{ backgroundColor: C.ink }}>Mixed</option>
          <option value="oneoff" style={{ backgroundColor: C.ink }}>One-off / project</option>
        </select>
      </Row>
      <Row label="TTM EBITDA ($)" hint="If positive, unlocks the EV/EBITDA method.">
        <NumberInput value={i.ttm_ebitda} onChange={(v) => patchInputs("ttm_ebitda", v)} testid="input-ebitda" />
      </Row>
    </SectionCard>
  );
}

// =============================================================== BERKUS
function BerkusTab({ doc, meta, patchInputs }) {
  const ticked = doc.inputs?.berkus_milestones || {};
  const toggle = (k) => patchInputs("berkus_milestones", { ...ticked, [k]: !ticked[k] });
  return (
    <SectionCard title="Berkus milestones" subtitle="Tick each de-risking milestone you've achieved. Adds KES 4–12M per milestone (per the methodology), max KES 50M pre-money.">
      <div className="space-y-2.5">
        {meta.berkus_milestones.map((m) => {
          const checked = !!ticked[m.id];
          return (
            <label key={m.id}
              className="flex items-start gap-3 p-3 rounded-md cursor-pointer transition-colors"
              style={{
                backgroundColor: checked ? "rgba(252, 211, 77, 0.06)" : "rgba(0,0,0,0.3)",
                border: `1px solid ${checked ? `${C.amber}55` : "rgba(255,255,255,0.07)"}`,
              }}
              data-testid={`berkus-${m.id}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(m.id)} className="mt-1 accent-amber-400" />
              <div className="flex-1">
                <div className="font-bold text-[14px] text-white">{m.label}</div>
                <div className="text-[11px] mt-0.5 font-mono-num" style={{ color: "rgba(255,255,255,0.5)" }}>KES {(m.value_kes[0]/1e6).toFixed(0)}M – {(m.value_kes[1]/1e6).toFixed(0)}M</div>
              </div>
            </label>
          );
        })}
      </div>
    </SectionCard>
  );
}

// =============================================================== SCORECARD
function ScorecardTab({ doc, meta, patchInputs }) {
  const scores = doc.inputs?.scorecard_factors || {};
  const setScore = (k, v) => patchInputs("scorecard_factors", { ...scores, [k]: Number(v) });
  return (
    <SectionCard title="Scorecard valuation" subtitle="Score each factor from 1 (well below median) to 5 (well above). Weighting follows Methodology Section 4.1.">
      {meta.scorecard_factors.map((f) => (
        <div key={f.id} className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <div>
              <div className="text-[13px] font-semibold text-white">{f.label}</div>
              <div className="text-[10px] font-mono-num" style={{ color: "rgba(255,255,255,0.4)" }}>Weight {f.weight}%</div>
            </div>
            <span className="text-sm font-mono-num font-bold" style={{ color: C.amber }}>{scores[f.id] || 3}/5</span>
          </div>
          <input type="range" min={1} max={5} step={1}
            value={scores[f.id] || 3}
            onChange={(e) => setScore(f.id, e.target.value)}
            data-testid={`scorecard-${f.id}`}
            className="w-full accent-amber-400" />
        </div>
      ))}
    </SectionCard>
  );
}

// =============================================================== FINANCIALS
function FinancialsTab({ doc, patchInputs }) {
  const i = doc.inputs || {};
  const fcf = i.fcf_5yr || ["", "", "", "", ""];
  const setFcf = (idx, v) => {
    const next = [...fcf];
    next[idx] = v;
    patchInputs("fcf_5yr", next.map((x) => (x === "" || x === null) ? 0 : Number(x)));
  };
  return (
    <SectionCard title="Forecast cash flows (DCF)" subtitle="Five-year free cash flow forecast — runs the methodology's 3-scenario DCF (Section 5.3.3).">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        {[0, 1, 2, 3, 4].map((y) => (
          <Row key={y} label={`F${y + 1} free cash flow ($)`}>
            <NumberInput value={fcf[y]} onChange={(v) => setFcf(y, v)} testid={`fcf-y${y + 1}`} />
          </Row>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Row label="WACC override (decimal)" hint="Blank → use sector default">
          <NumberInput value={i.wacc} onChange={(v) => patchInputs("wacc", v)} testid="input-wacc" step={0.01} />
        </Row>
        <Row label="Terminal growth (decimal)" hint="3–6% for African markets">
          <NumberInput value={i.terminal_growth} onChange={(v) => patchInputs("terminal_growth", v)} testid="input-terminal" step={0.01} />
        </Row>
      </div>
    </SectionCard>
  );
}

// =============================================================== BALANCE SHEET
function BalanceSheetTab({ doc, patchInputs }) {
  const i = doc.inputs || {};
  return (
    <SectionCard title="Balance sheet" subtitle="Used for the NAV floor (Section 5.5) and for the Enterprise→Equity bridge.">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Row label="Cash ($)">
          <NumberInput value={i.cash} onChange={(v) => patchInputs("cash", v)} testid="bs-cash" />
        </Row>
        <Row label="Total debt ($)">
          <NumberInput value={i.total_debt} onChange={(v) => patchInputs("total_debt", v)} testid="bs-debt" />
        </Row>
        <Row label="Total assets ($)">
          <NumberInput value={i.total_assets} onChange={(v) => patchInputs("total_assets", v)} testid="bs-assets" />
        </Row>
        <Row label="Total liabilities ($)">
          <NumberInput value={i.total_liabilities} onChange={(v) => patchInputs("total_liabilities", v)} testid="bs-liabilities" />
        </Row>
      </div>
    </SectionCard>
  );
}

// =============================================================== REPORT TAB
function ReportTab({ doc, calc }) {
  if (!calc || !calc.summary) {
    return <SectionCard title="Report"><p className="text-white/55 text-sm">Add some inputs to generate a preview — the report will appear here.</p></SectionCard>;
  }
  const dq = calc.data_quality;
  return (
    <div className="space-y-4">
      {/* Three-value table */}
      <SectionCard title="Three-value output" subtitle={`Method blend — ${calc.sector_label} · ${calc.sub_stage?.replace("_"," ")} stage`}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { key: "conservative_equity", label: "Conservative", color: "#94A3B8" },
            { key: "base_equity",         label: "Base case",   color: C.amber },
            { key: "optimistic_equity",   label: "Optimistic",   color: C.green },
          ].map((c) => (
            <div key={c.key} className="rounded-lg p-5"
              style={{ backgroundColor: "rgba(0,0,0,0.35)", border: `1px solid ${c.color}33` }}
              data-testid={`report-${c.key}`}>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: c.color }}>{c.label}</div>
              <div className="font-display font-black mt-2" style={{ fontSize: "1.8rem", letterSpacing: "-0.02em" }}>
                {fmt(calc.summary[c.key])}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-[12px]" style={{ color: "rgba(255,255,255,0.55)" }}>
          Net debt adjustment: <span className="font-mono-num text-white">{fmt(calc.summary.net_debt)}</span>
        </div>
      </SectionCard>

      {/* Methods breakdown */}
      <SectionCard title="Methods applied">
        {calc.methods.filter((m) => m.applicable).map((m) => (
          <div key={m.method} className="py-3 border-b last:border-0" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <div className="font-bold text-white capitalize">{m.method.replace("_", " ")}</div>
              <div className="text-[11px] font-mono-num" style={{ color: C.tealSoft }}>
                weight {(m.effective_weight * 100).toFixed(0)}%
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2 font-mono-num text-[13px]">
              <span style={{ color: "#94A3B8" }}>Cons {fmt(m.conservative_ev)}</span>
              <span style={{ color: C.amber }}>Base {fmt(m.base_ev)}</span>
              <span style={{ color: C.green }}>Opt {fmt(m.optimistic_ev)}</span>
            </div>
          </div>
        ))}
        {calc.methods.filter((m) => !m.applicable).map((m) => (
          <div key={m.method} className="py-2 text-[12px]" style={{ color: "rgba(255,255,255,0.4)" }}>
            <span className="font-bold capitalize">{m.method.replace("_", " ")}</span> skipped — {m.reason}
          </div>
        ))}
      </SectionCard>

      {/* Data quality + warnings */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SectionCard title="Data quality">
          <div className="flex items-baseline gap-3">
            <div className="font-display font-black text-4xl"
              style={{ color: dq.score >= 70 ? C.green : (dq.score >= 50 ? C.amber : C.red) }}>
              {dq.score}
            </div>
            <div>
              <div className="font-bold">{dq.label}</div>
              <div className="text-[11px] mt-1" style={{ color: "rgba(255,255,255,0.5)" }}>
                {dq.components.join(" · ")}
              </div>
            </div>
          </div>
        </SectionCard>
        <SectionCard title="Warnings">
          {(calc.warnings || []).length === 0 ? (
            <div className="text-[13px]" style={{ color: "rgba(255,255,255,0.55)" }}>No flags.</div>
          ) : (
            <ul className="space-y-2 text-[12px]">
              {calc.warnings.map((w, i) => (
                <li key={i} className="flex items-start gap-2" style={{ color: "rgba(255,255,255,0.7)" }}>
                  <Warning size={13} weight="fill" style={{ color: C.amber }} className="mt-0.5 shrink-0" />{w}
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

// =============================================================== Live preview rail
function LivePreview({ calc, doc, onFinalise, finalising }) {
  if (!calc || !calc.summary) {
    return (
      <div className="rounded-2xl p-5 border" style={{ borderColor: "rgba(123,211,206,0.18)", backgroundColor: C.ink }}>
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: C.tealSoft }}>Live preview</div>
        <p className="text-[13px] mt-2" style={{ color: "rgba(255,255,255,0.55)" }}>Add inputs to see your range update in real time.</p>
      </div>
    );
  }
  const dq = calc.data_quality;
  return (
    <>
      <div className="rounded-2xl p-5 border" style={{ borderColor: `${C.amber}33`, backgroundColor: C.ink }} data-testid="live-preview">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold mb-3" style={{ color: C.amber }}>
          Live preview · Equity value
        </div>
        <div className="space-y-2.5">
          {[
            { key: "conservative_equity", label: "Conservative", color: "#94A3B8" },
            { key: "base_equity",         label: "Base",         color: C.amber },
            { key: "optimistic_equity",   label: "Optimistic",   color: C.green },
          ].map((c) => (
            <div key={c.key} className="flex items-baseline justify-between">
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: c.color }}>{c.label}</span>
              <span className="font-mono-num font-extrabold text-lg" style={{ color: "#fff" }}>{fmt(calc.summary[c.key])}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t flex items-center justify-between"
          style={{ borderColor: "rgba(255,255,255,0.07)" }}>
          <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.5)" }}>Data quality</span>
          <span className="font-mono-num text-sm font-bold"
            style={{ color: dq.score >= 70 ? C.green : (dq.score >= 50 ? C.amber : C.red) }}>
            {dq.score}/100 · {dq.label}
          </span>
        </div>
      </div>

      {!doc.finalised_at && (
        <button onClick={onFinalise} disabled={finalising}
          data-testid="finalise-valuation-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-extrabold transition-colors disabled:opacity-60"
          style={{ backgroundColor: C.orange, color: "#000" }}>
          <Sparkle size={14} weight="fill" /> {finalising ? "Finalising…" : "Finalise · 50 credits"}
        </button>
      )}
      {doc.finalised_at && (
        <div className="rounded-md p-4 text-[12px]"
          style={{ backgroundColor: `${C.green}10`, border: `1px solid ${C.green}40`, color: C.green }}>
          <strong>Finalised.</strong> The report tab below shows the locked-in three-value output.
        </div>
      )}
    </>
  );
}

// =============================================================== shared bits
function SectionCard({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl p-6 border" style={{ borderColor: "rgba(123,211,206,0.12)", backgroundColor: C.ink }}>
      <h2 className="font-display font-extrabold text-xl tracking-tight">{title}</h2>
      {subtitle && <p className="text-[12px] mt-1 leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{subtitle}</p>}
      <div className="mt-5">{children}</div>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="mb-3">
      <label className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1.5 block" style={{ color: "rgba(255,255,255,0.55)" }}>{label}</label>
      {children}
      {hint && <div className="text-[10px] mt-1" style={{ color: "rgba(255,255,255,0.35)" }}>{hint}</div>}
    </div>
  );
}

function NumberInput({ value, onChange, testid, step }) {
  return (
    <input type="number" step={step || "1"}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      data-testid={testid}
      className="w-full px-3 py-2 rounded-md border focus:outline-none font-mono-num text-right"
      style={{ backgroundColor: "rgba(0,0,0,0.45)", borderColor: "rgba(255,255,255,0.1)", color: "#fff" }} />
  );
}
